import React from "react";
import Main from "../../../components/layout/Main";
import { Portal } from "../../../components/layout/app/Sheet";
import { UserService } from "../../../services/UserService";
import { User } from "../../../storage/store";
import "../../../assets/styles/app-settings.css";

// The app's alert dialog and the unmount guard live with the other shared
// pieces; the settings pages keep importing them from here.
export { default as AlertDialog } from "../../../components/layout/app/AlertDialog";
export { default as useAlive } from "../../../hooks/useAlive";

// Pieces shared by the device settings pages (app/screens/DeviceSettings/*).

// The app's default error toast (app/providers/DropDownProvider.js) and the
// settings screens' success toast.
export const DEFAULT_ERROR = "Something went wrong. Please try again.";
export const UPDATED = "Settings Updated successfully";

// Every settings command has this shape.
export const settingsPayload = (device, command) => ({
  on: [`${device.applianceId}`],
  off: [],
  action: { [device.applianceId]: command },
});

// The device's entry in the property config as the server has it now, so a
// form starts from current values rather than the copy the previous page was
// opened with. Null when the config can't be read or doesn't list the device.
export const readConfigDevice = async (applianceId) => {
  try {
    const config = await UserService.getConfig(User.getPropertyId());
    return config?.devices?.find((item) => item.applianceId === applianceId) ?? null;
  } catch (error) {
    return null;
  }
};

// Number boxes hold text; a missing value is an empty box.
export const toText = (value) => (value === undefined || value === null ? "" : String(value));

// True when the text is a number from min to max. An empty box is not 0.
export const inRange = (text, min, max) => {
  const value = String(text ?? "").trim();
  const number = Number(value);
  return value !== "" && Number.isFinite(number) && number >= min && number <= max;
};

export const clamp = (value, min, max) => (Number.isFinite(value) ? Math.min(max, Math.max(min, value)) : min);

// Leaves a settings page the way the header's back arrow does.
export const goBack = (history) => {
  if (history.length > 1) history.goBack();
  else history.push("/dashboard");
};

// The app's blocking loader (app/components/Loader/Loader.js): a white card
// with the message and a spinner over the dimmed page. The status line stays
// in the page so screen readers hear each message.
export const BusyOverlay = ({ message }) => (
  <>
    <p className="hx-set-sr" role="status">
      {message || ""}
    </p>
    {message ? (
      <Portal>
        <div className="hx-set-busy" aria-hidden="true">
          <div className="hx-set-busy-card">
            <span>{message}</span>
            <span className="hx-set-spinner" />
          </div>
        </div>
      </Portal>
    ) : null}
  </>
);

// Shell of every settings page: the back-arrow header (with the page's
// "Update" on the right), the device's name and the loader.
export const SettingsPage = ({ title, device, headerRight, busy, children }) => (
  <Main header="stack" title={title} headerRight={headerRight}>
    <div className="hx-set">
      {device?.name ? <p className="hx-set-caption">{device.name}</p> : null}
      {children}
    </div>
    <BusyOverlay message={busy} />
  </Main>
);

// The app's blue "Update". With form set it submits that form, so Enter in a
// field does the same. While busy it stays focusable; the page ignores it.
export const UpdateButton = ({ form, onClick, busy = false, disabled = false }) => (
  <button
    type={form ? "submit" : "button"}
    form={form}
    className="hx-set-update"
    onClick={onClick}
    disabled={disabled}
    aria-disabled={busy || undefined}
  >
    Update
  </button>
);

// Placeholder while a page reads the device's settings.
export const SettingsSkeleton = ({ tall = false }) => (
  <div>
    <p className="hx-set-sr">Please wait…</p>
    <div className={`hx-skel hx-set-skel ${tall ? "is-tall" : ""}`} aria-hidden="true" />
  </div>
);

// Shown instead of settings the device doesn't have.
export const Unavailable = ({ children = "These settings aren't available for this device." }) => (
  <div className="hx-empty hx-set-empty">{children}</div>
);

// A number box in the app's form style: purple label, the value, and a purple
// hint with the allowed range. Only digits go in, like the app's number pad.
export const NumberField = ({ id, label, value, placeholder, hint, invalid = false, onChange }) => (
  <div className={`hx-set-field ${invalid ? "is-invalid" : ""}`}>
    <label className="hx-set-label" htmlFor={id}>
      {label}
    </label>
    <input
      id={id}
      className="hx-input hx-set-input"
      type="text"
      inputMode="numeric"
      autoComplete="off"
      value={value}
      placeholder={placeholder}
      aria-invalid={invalid || undefined}
      aria-describedby={hint ? `${id}-hint` : undefined}
      onChange={(event) => onChange(event.target.value.replace(/\D/g, ""))}
    />
    {hint ? (
      <p id={`${id}-hint`} className="hx-set-hint">
        {hint}
      </p>
    ) : null}
  </div>
);

// The app's settings slider: label, slider, the value under it, then the hint.
// value is where the thumb sits; display is the text shown for the setting.
export const SliderField = ({ id, label, value, min, max, step, display, unit, hint, invalid = false, onChange }) => (
  <div className={`hx-set-field ${invalid ? "is-invalid" : ""}`}>
    <label className="hx-set-label" htmlFor={id}>
      {label}
    </label>
    <input
      id={id}
      className="hx-set-range"
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      aria-valuetext={`${display} ${unit}`}
      aria-invalid={invalid || undefined}
      aria-describedby={hint ? `${id}-hint` : undefined}
      onChange={(event) => onChange(Number(event.target.value))}
    />
    <output className="hx-set-slider-value" htmlFor={id}>
      {display}
    </output>
    {hint ? (
      <p id={`${id}-hint`} className="hx-set-hint">
        {hint}
      </p>
    ) : null}
  </div>
);

// "Motor 1" / "Motor 2" tabs, styled like the app's W2CM_1 curtain tabs.
// motors holds motor indexes (0 or 1); arrow keys move between the tabs.
export const MotorTabs = ({ id, panelId, motors, active, onChange }) => {
  const move = (event) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    const step = event.key === "ArrowRight" ? 1 : motors.length - 1;
    const next = motors[(motors.indexOf(active) + step) % motors.length];
    onChange(next);
    document.getElementById(`${id}-${next}`)?.focus();
  };

  return (
    <div className="hx-set-tabs" role="tablist" aria-label="Motor">
      {motors.map((motor) => (
        <button
          key={motor}
          id={`${id}-${motor}`}
          type="button"
          role="tab"
          aria-selected={motor === active}
          aria-controls={panelId}
          tabIndex={motor === active ? 0 : -1}
          className={`hx-set-tab ${motor === active ? "is-active" : ""}`}
          onClick={() => onChange(motor)}
          onKeyDown={move}
        >
          Motor {motor + 1}
        </button>
      ))}
    </div>
  );
};
